import { useEffect, useRef } from 'react';

import { ErrorBoundary } from '@datadog/browser-rum-react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Outlet, useLocation, useNavigate } from 'react-router';

import ErrorPage from '@/features/Home/ErrorPage';
import { routes } from '@/constants/Constants';
import getEnv from '@/shared/configs/env';
import { AnalyticsProvider, useAnalyticsContext } from '@/shared/providers/analytics';
import { useUrlChecker } from './useUrlChecker';
import { RouteEffects } from './route-effects';
import { trackOnClick } from '@/utils/trackOnClick';

function RouteErrorFallback({ resetError }: { error: Error; resetError: () => void }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const errorPathRef = useRef(pathname);

  useEffect(() => {
    if (pathname !== errorPathRef.current) {
      resetError();
    }
  }, [pathname, resetError]);

  return (
    <ErrorPage
      title="Something went wrong"
      subtitle="We could not load this page. Try going back to the homepage."
      onBack={() => {
        // Always leave the crashing route so the pathname-change effect can reset.
        navigate(pathname === routes.home ? routes.propertyEnrollmentForm : routes.home);
      }}
    />
  );
}

function RouteProviderContent() {
  const { isPublic } = useUrlChecker();
  const { track } = useAnalyticsContext();

  return (
    <HelmetProvider>
      <Helmet>
        <script
          type="text/javascript"
          id="hs-script-loader"
          async={true}
          defer={true}
          src={`//js.hs-scripts.com/${getEnv().hubspot_portal_id}.js`}
        />
        {isPublic && !location.pathname.includes('localhost') ? (
          <script
            id="cookieyes"
            type="text/javascript"
            async={true}
            src="https://cdn-cookieyes.com/client_data/f4c8903f602bc4de68ebe271/script.js"
          />
        ) : null}
      </Helmet>
      <RouteEffects />
      <div
        id="shell_container"
        role="button"
        tabIndex={0}
        onClick={(event) => trackOnClick(event, track)}
      >
        <Outlet />
      </div>
    </HelmetProvider>
  );
}

export function RouteProviders() {
  return (
    <ErrorBoundary fallback={RouteErrorFallback}>
      <AnalyticsProvider>
        <RouteProviderContent />
      </AnalyticsProvider>
    </ErrorBoundary>
  );
}
